import React, { useEffect, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import apiServices from "../../services/service";
import FormModalWrapper from "../../shared/modal/FormModalWrapper";
import SearchableDropDown from "../../shared/controls/form/searchableDropdown";
import DateBox from "../../shared/controls/form/dateBox";
import { ACCESS_LEVEL_OPTIONS, BUCKET_VISIBILITY } from "../../shared/constants";
import { Ic } from "../../shared/component/GosIcon";

const ACCESS_LEVEL_RANK = { L1: 1, L2: 2, L3: 3 };



const toDateOnlyString = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const getTodayString = () => toDateOnlyString(new Date());

const getMaxExpiresAtString = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return toDateOnlyString(d);
};

/* react-select styling to match the GrowthOS themed inputs */
const gosSelect = {
  control: (base, s) => ({
    ...base, minHeight: 42, borderRadius: 10, fontSize: 13.5,
    borderColor: s.isFocused ? "var(--gos-accent)" : "var(--gos-border-strong)",
    boxShadow: s.isFocused ? "0 0 0 3px var(--gos-accent-soft)" : "none",
    backgroundColor: "var(--gos-surface)", "&:hover": { borderColor: "var(--gos-accent)" },
  }),
  menu: (b) => ({ ...b, zIndex: 9999, backgroundColor: "var(--gos-surface)", border: "1px solid var(--gos-border-strong)", borderRadius: 11, overflow: "hidden" }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  option: (b, s) => ({ ...b, fontSize: 13.5, backgroundColor: s.isSelected ? "var(--gos-accent-soft)" : s.isFocused ? "var(--gos-surface-3)" : "transparent", color: s.isSelected ? "var(--gos-accent-ink)" : "var(--gos-text)" }),
  placeholder: (b) => ({ ...b, color: "var(--gos-text-3)" }),
  singleValue: (b) => ({ ...b, color: "var(--gos-text)" }),
  input: (b) => ({ ...b, color: "var(--gos-text)" }),
  multiValue: (b) => ({ ...b, backgroundColor: "var(--gos-accent-soft)", borderRadius: 6 }),
  multiValueLabel: (b) => ({ ...b, fontSize: 12, color: "var(--gos-accent-ink)" }),
  indicatorSeparator: (b) => ({ ...b, backgroundColor: "var(--gos-border)" }),
  dropdownIndicator: (b) => ({ ...b, color: "var(--gos-text-3)" }),
};

const reqStar = <span style={{ color: "var(--gos-red-fg)" }}> *</span>;
const errText = (msg) =>
  msg ? <div style={{ color: "var(--gos-red-fg)", fontSize: 12, marginTop: 5 }}>{msg}</div> : null;
const fieldWrap = { marginBottom: 16 };

const AddBucketForm = ({ open, onClose, onSave, usersList = [], editData = null }) => {
  const isEdit = !!editData;
  const [bucketName, setBucketName] = useState("");
  const [owner, setOwner] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null);
  const [visibility, setVisibility] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [expiresAt, setExpiresAt] = useState(getMaxExpiresAtString);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const userOptions = usersList.map((u) => ({
    value: u.userId,
    label:
      `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
      u.userName ||
      u.emailAddress ||
      u.email ||
      "",
    accessLevel: u.accessLevel,
  }));

  const isPrivate = visibility?.value === "Private";

  // Only users at or above the bucket's access level can own it or be granted private access.
  const accessLevelRank = ACCESS_LEVEL_RANK[accessLevel?.value] ?? null;
  const assignableUserOptions = accessLevelRank
    ? userOptions.filter((u) => ACCESS_LEVEL_RANK[u.accessLevel] >= accessLevelRank)
    : userOptions;

  const reset = () => {
    setBucketName("");
    setOwner(null);
    setAccessLevel(null);
    setVisibility(null);
    setAssignedUsers([]);
    setExpiresAt(getMaxExpiresAtString());
    setErrors({});
  };

  // Populate fields from the bucket being edited, or reset to blank defaults for a fresh Add
  useEffect(() => {
    if (!open) return;
    if (editData) {
      setBucketName(editData.name || "");
      setOwner(
        userOptions.find((o) => String(o.value) === String(editData.bucketOwnerId)) ||
        (editData.bucketOwnerId
          ? { value: editData.bucketOwnerId, label: editData.bucketOwnerFullName || editData.bucketOwnerId }
          : null)
      );
      setAccessLevel(ACCESS_LEVEL_OPTIONS.find((o) => o.value === editData.accessLevel) || null);
      setVisibility(BUCKET_VISIBILITY.find((o) => o.value === editData.bucketVisibility) || null);
      // Private buckets carry their current grants as a `users` array ({ userId, userFullName, ... })
      setAssignedUsers(
        (editData.users || []).map((u) => {
          const match = userOptions.find((o) => String(o.value) === String(u.userId));
          return match || { value: u.userId, label: u.userFullName || u.userId };
        })
      );
      setExpiresAt(
        editData.expiryDateTime ? toDateOnlyString(new Date(editData.expiryDateTime)) : getMaxExpiresAtString()
      );
    } else {
      reset();
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editData]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = () => {
    const newErrors = {};
    if (!bucketName.trim()) newErrors.bucketName = "Bucket name is required";
    if (!owner) newErrors.owner = "Bucket owner is required";
    if (!accessLevel) newErrors.accessLevel = "Access level is required";
    if (!visibility) newErrors.visibility = "Bucket visibility is required";
    if (visibility?.value === "Private" && !assignedUsers.length)
      newErrors.assignedUsers = "Please assign at least one user";
    if (!expiresAt) newErrors.expiresAt = "Expiry date is required";
    else if (expiresAt < getTodayString() || expiresAt > getMaxExpiresAtString())
      newErrors.expiresAt = "Expiry date must be between today and one year from today";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    const basePayload = {
      name: bucketName.trim(),
      bucketOwnerId: owner?.value ?? null,
      accessLevel: accessLevel?.value ?? null,
      bucketVisibility: visibility?.value ?? null,
      expiryDateTime: expiresAt,
    };

    setSaving(true);

    let request;
    if (isEdit) {
      // Bucket fields go through Update; private-bucket membership goes through its own
      // grant endpoint in parallel (Update never carries userIds).
      const updateRequest = apiServices.updateBucket({ id: editData.id, ...basePayload });
      request =
        visibility?.value === "Private"
          ? Promise.all([
            updateRequest,
            apiServices.updateBucketMembers({
              bucketId: editData.id,
              userIds: assignedUsers.map((u) => u.value).join(","),
            }),
          ]).then(([updateResponse]) => updateResponse)
          : updateRequest;
    } else {
      request = apiServices.addBucket({
        ...basePayload,
        userIds: visibility?.value === "Private" ? assignedUsers.map((u) => u.value).join(",") : null,
      });
    }

    request
      .then((response) => {
        // Refetch so we hand the parent the actual saved record (owner name, users, etc.)
        // rather than whatever Update's own response happens to echo back.
        if (isEdit) {
          return apiServices.getBuckets().then((listResponse) => {
            const raw = listResponse.data;
            const list = Array.isArray(raw) ? raw : raw?.bucketList || raw?.buckets || raw?.data || [];
            return list.find((b) => String(b.id) === String(editData.id)) || response.data;
          });
        }
        return response.data;
      })
      .then((data) => {
        toast.success(isEdit ? "Bucket updated successfully!" : "Bucket added successfully!", { position: "top-right" });
        onSave(data);
        reset();
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message ||
          error.message ||
          (isEdit ? "Failed to update bucket" : "Failed to add bucket"),
          { position: "top-right" }
        );
      })
      .finally(() => setSaving(false));
  };

  if (!open) return null;

  return (
    <div
      className="gos-import-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) handleClose(); }}
    >
      <div className="gos-import-modal" style={{ maxWidth: 520 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic"><Ic name="inbox" size={18} /></span>
            <div>
              <div className="gos-import-title">{isEdit ? "Edit bucket" : "New bucket"}</div>
              <div className="gos-import-sub">Pool unassigned records and control who can pull from them.</div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={handleClose} disabled={saving}>
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-import-body" style={{ padding: "16px" }}>
          <div style={fieldWrap}>
            <label className="gos-tm-flabel" htmlFor="bucketName">Bucket name{reqStar}</label>
            <input
              id="bucketName"
              className={"gos-tm-input" + (errors.bucketName ? " err" : "")}
              type="text"
              value={bucketName}
              onChange={(e) => {
                setBucketName(e.target.value);
                if (errors.bucketName) setErrors((prev) => ({ ...prev, bucketName: undefined }));
              }}
              placeholder="e.g. Unassigned Leads"
            />
            {errText(errors.bucketName)}
          </div>

          <div style={{ ...fieldWrap, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label className="gos-tm-flabel">Access level{reqStar}</label>
              <Select
                options={ACCESS_LEVEL_OPTIONS}
                value={accessLevel}
                placeholder="Select level"
                menuPortalTarget={document.body}
                styles={gosSelect}
                onChange={(selected) => {
                  setAccessLevel(selected);
                  if (errors.accessLevel) setErrors((prev) => ({ ...prev, accessLevel: undefined }));
                  // Drop any assigned users / owner who no longer meet the new access level's floor
                  const newRank = ACCESS_LEVEL_RANK[selected?.value] ?? null;
                  if (newRank) {
                    setAssignedUsers((prev) => prev.filter((u) => ACCESS_LEVEL_RANK[u.accessLevel] >= newRank));
                    if (!isEdit) {
                      setOwner((prev) => (prev && ACCESS_LEVEL_RANK[prev.accessLevel] >= newRank ? prev : null));
                    }
                  }
                }}
              />
              {errText(errors.accessLevel)}
            </div>
            <div>
              <label className="gos-tm-flabel" htmlFor="expiresAt">Expires at{reqStar}</label>
              <input
                id="expiresAt"
                className={"gos-tm-input" + (errors.expiresAt ? " err" : "")}
                type="date"
                value={expiresAt}
                min={getTodayString()}
                max={getMaxExpiresAtString()}
                onChange={(e) => {
                  setExpiresAt(e.target.value);
                  if (errors.expiresAt) setErrors((prev) => ({ ...prev, expiresAt: undefined }));
                }}
              />
              {errText(errors.expiresAt)}
            </div>
          </div>

          <div style={fieldWrap}>
            <label className="gos-tm-flabel">Bucket owner{reqStar}</label>
            <Select
              options={assignableUserOptions}
              value={owner}
              placeholder={accessLevel ? "Select owner" : "Select an access level first"}
              isDisabled={isEdit}
              noOptionsMessage={() => "No users at this access level or higher"}
              menuPortalTarget={document.body}
              styles={gosSelect}
              onChange={(selected) => {
                setOwner(selected);
                if (errors.owner) setErrors((prev) => ({ ...prev, owner: undefined }));
              }}
            />
            {errText(errors.owner)}
          </div>

          <div style={fieldWrap}>
            <label className="gos-tm-flabel">Bucket visibility{reqStar}</label>
            <Select
              options={BUCKET_VISIBILITY}
              value={visibility}
              placeholder="Select visibility"
              isDisabled={isEdit}
              menuPortalTarget={document.body}
              styles={gosSelect}
              onChange={(selected) => {
                setVisibility(selected);
                if (errors.visibility) setErrors((prev) => ({ ...prev, visibility: undefined }));
                if (selected?.value !== "Private") {
                  setAssignedUsers([]);
                  if (errors.assignedUsers) setErrors((prev) => ({ ...prev, assignedUsers: undefined }));
                }
              }}
            />
            {errText(errors.visibility)}
          </div>

          {isPrivate && (
            <div style={{ marginBottom: 4 }}>
              <label className="gos-tm-flabel">
                Assign users{reqStar}{" "}
                <span style={{ color: "var(--gos-text-3)", fontWeight: 500 }}>({assignedUsers.length} selected)</span>
              </label>
              <Select
                isMulti
                options={assignableUserOptions}
                value={assignedUsers}
                placeholder="Search and select users…"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                menuPortalTarget={document.body}
                styles={gosSelect}
                onChange={(selected) => {
                  setAssignedUsers(selected || []);
                  if (errors.assignedUsers) setErrors((prev) => ({ ...prev, assignedUsers: undefined }));
                }}
              />
              {errText(errors.assignedUsers)}
            </div>
          )}
        </div>

        <div className="gos-import-foot end">
          <div className="gos-foot-actions">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={handleClose} disabled={saving}>Cancel</button>
            <button type="button" className="gos-btn gos-btn-primary" onClick={handleSave} disabled={saving}>
              <Ic name="check" size={16} />
              {saving ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save bucket" : "Create bucket"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBucketForm;
